import ArrowBackIcon from '@material-design-icons/svg/filled/arrow_back.svg?react';
import GroupIcon from '@material-design-icons/svg/filled/group.svg?react';
import KeyIcon from '@material-design-icons/svg/filled/key.svg?react';
import PersonIcon from '@material-design-icons/svg/filled/person.svg?react';
import SettingsIcon from '@material-design-icons/svg/filled/settings.svg?react';
import TuneIcon from '@material-design-icons/svg/filled/tune.svg?react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router';
import { canAccessApiKeyManagement } from '../../utils/apiKeyAccess';

interface SettingsViewProps {
  onBackToApp: () => void;
  onMyProfileClick: () => void;
  shouldAnimate?: boolean;
  onLinkClick?: () => void;
  canManageTeam?: boolean;
  hasPublisherProfile?: boolean;
}

const settingsItems = [
  {
    icon: <SettingsIcon />,
    label: 'Publisher Profile',
    id: 'publisher-profile',
    route: '/dashboard/settings/publisher',
  },
  {
    icon: <PersonIcon />,
    label: 'My Profile',
    id: 'my-profile',
    route: '/dashboard/settings/profile',
  },
  {
    icon: <TuneIcon />,
    label: 'Preferences',
    id: 'preferences',
    route: '/dashboard/settings/preferences',
  },
  {
    icon: <GroupIcon />,
    label: 'Team',
    id: 'team',
    route: '/dashboard/settings/team',
    requiresManageTeam: true,
  },
  {
    icon: <KeyIcon />,
    label: 'API Keys',
    id: 'developer-api-keys',
    route: '/dashboard/settings/api-keys',
    requiresManageTeam: true,
  },
];

export const SettingsView = ({
  onBackToApp,
  onMyProfileClick,
  shouldAnimate = true,
  onLinkClick,
  canManageTeam,
  hasPublisherProfile = true,
}: SettingsViewProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const visibleSettingsItems = settingsItems.filter(
    (item) =>
      (item.id !== 'publisher-profile' || hasPublisherProfile) &&
      (!item.requiresManageTeam || canAccessApiKeyManagement(canManageTeam)),
  );

  const isActiveItem = (route: string) => {
    return currentPath === route;
  };

  const handleItemClick = (itemId: string, route: string) => {
    if (itemId === 'my-profile') {
      onMyProfileClick();
    } else {
      navigate(route);
      onLinkClick?.();
    }
  };
  return (
    <motion.div
      key="settings"
      initial={shouldAnimate ? { x: '100%' } : false}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{
        type: 'spring',
        stiffness: 650,
        damping: 38,
        mass: 0.5,
        velocity: 3,
      }}
      className="absolute inset-0 flex flex-col justify-between w-full h-full"
    >
      <div className="flex flex-col justify-start items-start flex-1">
        <div className="pt-2 flex flex-col justify-center items-center gap-3 w-full">
          <div className="w-full flex flex-col justify-start items-center relative">
            <div className="w-64 h-14 px-4 py-5 bg-black/60 inline-flex justify-start items-center gap-2">
              <button
                type="button"
                onClick={onBackToApp}
                className="flex items-center gap-2 text-white hover:text-gray-300 transition-colors duration-200"
              >
                <ArrowBackIcon className="size-5 fill-current text-white" />
                <span className="text-sm font-bold leading-tight">
                  Back to App
                </span>
              </button>
            </div>

            <div className="h-0 outline-1 outline-offset-[-0.50px] outline-neutral-800 w-full"></div>
          </div>
        </div>

        <div className="px-2 pt-4 pb-2 flex flex-col justify-start items-start gap-2 w-full overflow-y-auto flex-1">
          <div className="w-40 h-5 relative px-2">
            <div className="opacity-70 text-white text-xs font-semibold uppercase leading-tight tracking-tight">
              ACCOUNT
            </div>
          </div>

          <div className="flex flex-col justify-start items-start w-full">
            {visibleSettingsItems.map((item, index) => {
              const isActive = isActiveItem(item.route);
              return (
                <div key={item.label} className={index > 0 ? 'mt-1' : ''}>
                  <button
                    type="button"
                    aria-label={item.label}
                    onClick={() => handleItemClick(item.id, item.route)}
                    className={`w-60 h-9 px-2 py-5 rounded-xl inline-flex justify-start items-center gap-2 transition-all duration-300 ease-out cursor-pointer relative group ${
                      isActive
                        ? 'bg-[#151717] shadow-[0px_0.375px_0.375px_0px_inset_rgba(255,255,255,0.17)]'
                        : ''
                    } hover:bg-[#151717] hover:shadow-[0px_0.375px_0.375px_0px_inset_rgba(255,255,255,0.17)]`}
                  >
                    <div className="flex justify-start items-center gap-2.5">
                      <div className="size-5 flex items-center justify-center">
                        <div
                          className={`w-5 h-5 fill-current transition-all duration-300 ease-out [&>svg]:w-full [&>svg]:h-full ${
                            isActive
                              ? 'text-white'
                              : 'text-[#96A3A3] group-hover:text-white'
                          }`}
                        >
                          {item.icon}
                        </div>
                      </div>
                      <div
                        className={`justify-start text-sm font-medium font-['Inter'] leading-tight transition-all duration-300 ease-out ${
                          isActive
                            ? 'text-white'
                            : 'text-[#96A3A3] group-hover:text-white'
                        }`}
                      >
                        {item.label}
                      </div>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
